import { Component, OnInit, Input, Output, OnChanges, SimpleChanges, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
// services
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import * as words from '../../../../services/language'
import { Receivable } from 'src/app/models/receivable.model';
import { formatCurrency } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { Subscription } from 'rxjs';
import moment from 'moment';

@Component({
    selector: 'payoff-modal',
    templateUrl: './payoff-modal.component.html',
    styleUrls: ['./payoff-modal.component.scss'],
    standalone: false
})
export class PayoffModal implements OnInit, OnChanges, OnDestroy {
    
    // The current state of the loan terms to be paid off.
    @Input() receivable: Receivable
    
    // Currently selected language
    @Input() language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'
    
    // Word replacements for each language.
    @Input() words: Object = words.language

    /** Indicates to the callee that the user chose to proceed with the payoff and it was successful. */
    @Output() paidOff: EventEmitter<Object> = new EventEmitter()

    // Top level payoff form with basic details.
    protected form: FormGroup;

    private subscriptions: Subscription[] = []
    
    // Indicates whether the payoff is currently processing.
    protected processing: boolean = false

    constructor(private master: MasterService, private ms: MessageService, private store: StoreService, private fb: FormBuilder) { 
        this.form = this.fb.group({
            last: new FormControl<string>(moment().format('YYYY-MM-DD'), []),
            when: new FormControl<string>(moment().format('YYYY-MM-DD'), []),
            discount: new FormControl<string>(formatCurrency(0), [])
        })

        this.form.get('last').disable({emitEvent: false})
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('when').valueChanges.subscribe((mod) => {
                if (moment(mod).isBefore(moment(this.form.get('last').value))) {
                    this.ms.sendMessage("alert", { type: "danger", text: 'Payoff date must be after the last payment date and sold date.' })
                    this.form.get('when').setValue(moment().format('YYYY-MM-DD'), {emitEvent: false})
                    return
                }

                this.fetchPayoff()
            })
        )
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.receivable) {
            this.form.get('last').setValue(moment(this.receivable.prevPaymentDate || this.receivable.sold).format('YYYY-MM-DD'), {emitEvent: false})
            this.form.get('when').setValue(moment().format('YYYY-MM-DD'), {emitEvent: false})
            this.form.get('discount').setValue(formatCurrency(this.receivable.payoff), {emitEvent: false})
        }
    }

    /**
     * User changed the payoff date so we need to check the payoff amount at that time.
     */
    protected fetchPayoff() {
        if (!this.receivable || !this.receivable.termsId || (this.receivable.termsId <= 0)) {
            return
        }

        const asOf = moment(this.form.get('when').value).format('YYYY-MM-DD')

        this.master.get(`receivables/${this.receivable.termsId}?asOf=${asOf}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            const result = res.data
            if (!result || !result.payoff) {
                return
            }

            this.form.get('discount').setValue(formatCurrency(result.payoff), {emitEvent: false})
            this.receivable.payoff = result.payoff
        })
    }

    /**
     * User has asked to payoff the sale.
     * @param {Event} e Event that triggered the payoff.
     */
    protected payoff(e: Event) {
        if (this.processing || !this.receivable || !this.receivable.saleId || (this.receivable.saleId <= 0)) {
            return
        }

        if (e) {
            e.stopPropagation()
            e.preventDefault()
        }

        const payload = {
            payoffDate: this.form.get('when').value,
            discount: clean(this.form.get('discount').value)
        }

        this.processing = true
        this.master.patch(`sales/${this.receivable.saleId}/payoff`, payload, (res) => {
            this.processing = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            this.paidOff.emit(payload)
        })
    }
}
